/*
 * /teething-calendar — когда режутся и когда выпадают зубы у ребёнка.
 *
 * Спрос ~26 800/мес закрыт статичными картинками «6–10 месяцев» и парой
 * ползунков возраста. Разница между ними и этим тулом в четырёх вещах:
 *
 *  1. ДАТЫ, а не диапазоны месяцев. От дня рождения ребёнка считается
 *     конкретное окно: «с 12 марта по 2 июля». «В 8–12 месяцев» родитель
 *     всё равно переводит в даты в уме, и ошибается.
 *
 *  2. ПОПРАВКА НА НЕДОНОШЕННОСТЬ. Прорезывание идёт по СКОРРЕКТИРОВАННОМУ
 *     возрасту — от предполагаемой даты родов, а не от фактической. У
 *     ребёнка, родившегося на 30-й неделе, отставание в два с половиной
 *     месяца — норма, а не тревога. Ни один просмотренный конкурент этого
 *     не делает, и родители таких детей получают ложную тревогу штатно.
 *
 *  3. ОБЕ ФАЗЫ. Прорезывание (0–3 года) и смена (6–12 лет) — это один и тот
 *     же вопрос «что происходит у ребёнка во рту», а тулы делают только
 *     первую половину.
 *
 *  4. ДНЕВНИК. Отмеченная дата остаётся и сравнивается с нормой, а сама
 *     карточка становится тем, что хранят.
 */

import { Odontogram } from './odontogram.js';

const CFG = window.__TEETHING || {};
const T = CFG.i18n || {};
const $ = s => document.querySelector(s);
const $$ = s => Array.from(document.querySelectorAll(s));

const KEY = 'teething-calendar:v1';
const DAY = 86400000;
const MONTH = 30.4375;   // средняя длина месяца в днях

const state = {
    birth: null,
    weeks: 40,            // срок родов в неделях — для поправки
    log: {},              // fdi → { erupted, shed, approx }
    absent: {},           // fdi → фаза, в которой родитель подтвердил отсутствие
};

let chart = null;

// ── запуск ──────────────────────────────────────────────────────────────

function boot() {
    restore();

    chart = new Odontogram($('#tgChart'), {
        teeth: CFG.teeth,
        dentition: 'primary',
        surfaces: false,
        labels: { names: CFG.names, chartLabel: T.chart_label },
        onPick: ({ fdi }) => openTooth(fdi),
    });

    $('#tgBirth').addEventListener('change', () => {
        const v = $('#tgBirth').value;
        state.birth = v ? new Date(v + 'T12:00:00') : null;
        save(); render();
        if (window.__aeFunnel) window.__aeFunnel('birth_set');
    });

    $('#tgWeeks').addEventListener('change', () => {
        state.weeks = Math.max(24, Math.min(42, +$('#tgWeeks').value || 40));
        $('#tgWeeks').value = state.weeks;
        save(); render();
    });

    $('#tgPhase').addEventListener('click', e => {
        const b = e.target.closest('[data-val]');
        if (!b) return;
        setPhase(b.getAttribute('data-val'));
    });

    $('#tgPrint').addEventListener('click', () => window.print());
    $('#tgReset').addEventListener('click', () => {
        if (!confirm(T.reset_confirm)) return;
        state.log = {}; save(); render();
    });

    render();
    if (window.__stage) window.__stage('ready');
}

function restore() {
    try {
        const raw = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (raw) {
            state.birth = raw.birth ? new Date(raw.birth) : null;
            state.weeks = raw.weeks || 40;
            state.log = raw.log || {};
            state.absent = raw.absent || {};
        }
    } catch (_) {}
    if (state.birth) $('#tgBirth').value = isoDate(state.birth);
    $('#tgWeeks').value = state.weeks;
}

function save() {
    try {
        localStorage.setItem(KEY, JSON.stringify({
            birth: state.birth ? state.birth.toISOString() : null,
            weeks: state.weeks, log: state.log, absent: state.absent || {},
        }));
    } catch (_) {}
}

// ── возраст ─────────────────────────────────────────────────────────────

/**
 * Скорректированный возраст в месяцах. Для доношенного (40 недель) он равен
 * фактическому; для родившегося на 30-й неделе он на 10 недель меньше — и
 * именно по нему идёт прорезывание.
 */
function ageMonths(at = Date.now()) {
    if (!state.birth) return null;
    const days = (at - state.birth.getTime()) / DAY;
    const correction = state.weeks < 37 ? (40 - state.weeks) * 7 : 0;
    return (days - correction) / MONTH;
}

function correctionMonths() {
    return state.weeks < 37 ? ((40 - state.weeks) * 7) / MONTH : 0;
}

/** Дата, соответствующая скорректированному возрасту в месяцах. */
function dateAt(months) {
    if (!state.birth) return null;
    const correction = state.weeks < 37 ? (40 - state.weeks) * 7 : 0;
    return new Date(state.birth.getTime() + (months * MONTH + correction) * DAY);
}

// ── фазы ────────────────────────────────────────────────────────────────

function currentPhase() {
    const saved = $$('#tgPhase [data-val]').find(b => b.classList.contains('on'));
    return saved ? saved.getAttribute('data-val') : 'auto';
}

function setPhase(v) {
    $$('#tgPhase [data-val]').forEach(b => {
        const on = b.getAttribute('data-val') === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    render();
}

function effectivePhase() {
    const p = currentPhase();
    if (p !== 'auto') return p;
    const m = ageMonths();
    if (m == null) return 'erupt';
    return m >= 66 ? 'shed' : 'erupt';   // ~5.5 года — начало смены
}

// ── статусы зубов ───────────────────────────────────────────────────────

/**
 * Статус зуба на СЕГОДНЯ:
 *   logged  — родитель отметил дату
 *   due     — окно открыто прямо сейчас
 *   unknown — окно закрылось, отметки нет, и родитель ещё не сказал, вышел
 *             зуб или нет
 *   late    — окно закрылось, и родитель подтвердил, что зуба нет
 *   soon    — окно откроется в ближайшие месяцы
 *   future  — ещё далеко
 *
 * ⚠️⚠️ Разделение `unknown` и `late` — не педантизм. Без него тул красным
 * сообщает родителю десятилетнего ребёнка, что у него «8 зубов за сроком»,
 * хотя они выпали давно, просто дневник никто не вёл. Инструмент НЕ ЗНАЕТ
 * того, чего ему не сказали, и обязан это признавать, а не выдавать
 * незнание за тревогу. Тот же приём — в /vaccination-calendar
 * («Отметьте, что уже сделано»).
 */
function statusOf(fdi, phase) {
    const t = CFG.teeth[fdi];
    const m = ageMonths();
    const log = state.log[fdi] || {};

    const absent = (state.absent || {})[fdi] === phase;

    if (phase === 'shed') {
        if (log.shed) return 'logged';
        if (!t.shed || m == null) return 'future';
        const [a, b] = t.shed;
        if (m > b) return absent ? 'late' : 'unknown';
        if (m >= a) return 'due';
        return m >= a - 6 ? 'soon' : 'future';
    }

    if (log.erupted) return 'logged';
    if (m == null) return 'future';
    const [a, b] = t.erupt;
    if (m > b) return absent ? 'late' : 'unknown';
    if (m >= a) return 'due';
    return m >= a - 2 ? 'soon' : 'future';
}

// `soon` красится тоже, но мягко: у шестилетки иначе вся схема пустая, и
// родитель решает, что инструмент сломан.
const SEV = {
    logged: 'done', due: 'attention', late: 'urgent',
    unknown: null, soon: 'soon', future: null,
};

// ── отрисовка ───────────────────────────────────────────────────────────

function render() {
    const has = !!state.birth;
    $('#tgEmpty').hidden = has;
    $('#tgBody').hidden = !has;
    $('#tgCorrection').hidden = correctionMonths() < 0.5;
    if (correctionMonths() >= 0.5) {
        $('#tgCorrection').textContent = fmt(T.correction, {
            weeks: state.weeks,
            months: correctionMonths().toFixed(1),
        });
    }
    if (!has) return;

    const phase = effectivePhase();
    const list = phase === 'shed' ? Object.keys(CFG.teeth).filter(f => CFG.teeth[f].primary)
        : Object.keys(CFG.teeth).filter(f => CFG.teeth[f].primary);

    // Схема: молочные всегда, потому что обе фазы про них.
    const marks = {};
    for (const fdi of list) {
        const st = statusOf(fdi, phase);
        if (phase === 'shed' && st === 'logged') marks[fdi] = { whole: 'missing' };
        else if (SEV[st]) marks[fdi] = { severity: SEV[st] };
    }
    chart.setMarks(marks);

    renderSummary(list, phase);
    renderUnknown(list, phase);
    renderList(list, phase);
    renderNotes(phase);
}

function renderSummary(list, phase) {
    const m = ageMonths();
    $('#tgAge').textContent = humanAge(m);

    const counts = { logged: 0, due: 0, late: 0, soon: 0, future: 0 };
    for (const fdi of list) counts[statusOf(fdi, phase)]++;

    $('#tgDone').textContent = String(counts.logged);
    $('#tgDoneLabel').textContent = phase === 'shed' ? T.sum_shed : T.sum_erupted;
    $('#tgDue').textContent = String(counts.due);
    $('#tgLate').textContent = String(counts.late);
    $('#tgLateBox').hidden = counts.late === 0;
}

/**
 * «Мы не знаем» вместо «у вас беда». Пока родитель не сказал, что стало с
 * зубами, чей срок прошёл, инструмент предлагает ответить одной кнопкой — и
 * только отрицательный ответ превращает их в тревожные.
 */
function renderUnknown(list, phase) {
    const unknown = list.filter(f => statusOf(f, phase) === 'unknown');
    const box = $('#tgUnknown');
    box.hidden = unknown.length === 0;
    if (!unknown.length) return;

    $('#tgUnknownText').textContent = fmt(
        phase === 'shed' ? T.unknown_shed : T.unknown_erupt,
        { n: unknown.length }
    );

    $('#tgUnknownYes').onclick = () => {
        // Датой ставим СЕРЕДИНУ окна, а не сегодня: «выпал сегодня» у зуба,
        // выпавшего три года назад, — это неправда, записанная нашей рукой.
        for (const fdi of unknown) {
            const t = CFG.teeth[fdi];
            const [a, b] = phase === 'shed' ? t.shed : t.erupt;
            state.log[fdi] = Object.assign({}, state.log[fdi], {
                [phase === 'shed' ? 'shed' : 'erupted']: isoDate(dateAt((a + b) / 2)),
                approx: true,
            });
        }
        save(); render();
        if (window.__aeFunnel) window.__aeFunnel('bulk_confirm', { phase, n: unknown.length });
    };

    $('#tgUnknownNo').onclick = () => {
        state.absent = state.absent || {};
        for (const fdi of unknown) state.absent[fdi] = phase;
        save(); render();
    };
}

function renderList(list, phase) {
    const box = $('#tgList');
    box.textContent = '';

    const rows = list.map(fdi => {
        const t = CFG.teeth[fdi];
        const [a, b] = phase === 'shed' ? t.shed : t.erupt;
        return { fdi, t, from: a, to: b, st: statusOf(fdi, phase) };
    });

    // Порядок — по ожидаемому сроку, а не по номеру: родителю важно «что
    // следующее», а не «какой квадрант».
    const rank = { due: 0, late: 1, unknown: 2, soon: 3, future: 4, logged: 5 };
    rows.sort((x, y) => (rank[x.st] - rank[y.st]) || (x.from - y.from));

    for (const r of rows) {
        const row = document.createElement('div');
        row.className = 'tg-row st-' + r.st;

        const name = document.createElement('b');
        name.textContent = CFG.names[r.fdi];

        const when = document.createElement('span');
        when.className = 'tg-when';
        const log = state.log[r.fdi] || {};
        const loggedDate = phase === 'shed' ? log.shed : log.erupted;
        if (loggedDate && log.approx) {
            // Дата проставлена оптом, по середине окна. Выдавать её за
            // наблюдение родителя нельзя — подписываем как приблизительную.
            when.textContent = fmt(T.approx, { date: fmtDate(new Date(loggedDate)) });
        } else if (loggedDate) {
            const dev = deviation(r, loggedDate);
            when.textContent = fmtDate(new Date(loggedDate)) + (dev ? ' · ' + dev : '');
        } else {
            const d1 = dateAt(r.from), d2 = dateAt(r.to);
            when.textContent = fmt(T.window, { from: fmtDate(d1), to: fmtDate(d2) });
        }

        const badge = document.createElement('i');
        badge.className = 'tg-badge';
        badge.textContent = T.status[r.st];

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tc-btn sm ' + (loggedDate ? 'ghost' : '');
        btn.textContent = loggedDate ? T.btn_edit
            : (phase === 'shed' ? T.btn_mark_shed : T.btn_mark_erupt);
        btn.addEventListener('click', () => openTooth(r.fdi));

        row.append(name, when, badge, btn);
        box.appendChild(row);
    }
}

/** Насколько отметка родителя разошлась с нормой. */
function deviation(r, isoDay) {
    const m = ageMonths(new Date(isoDay + 'T12:00:00').getTime());
    if (m == null) return '';
    if (m < r.from) return fmt(T.dev_early, { n: (r.from - m).toFixed(1) });
    if (m > r.to) return fmt(T.dev_late, { n: (m - r.to).toFixed(1) });
    return T.dev_norm;
}

/**
 * Заметки, которые появляются РОВНО тогда, когда становятся нужны.
 * Это и есть разница между статьёй и инструментом: статья рассказывает про
 * «шестёрку» всем и всегда, а здесь она появляется у ребёнка, которому
 * пять с половиной.
 */
function renderNotes(phase) {
    const m = ageMonths();
    const box = $('#tgNotes');
    box.textContent = '';

    const add = (key, kind) => {
        const d = document.createElement('div');
        d.className = 'tg-note' + (kind ? ' tg-note-' + kind : '');
        const h = document.createElement('b');
        h.textContent = T.note[key].t;
        const p = document.createElement('span');
        p.textContent = T.note[key].d;
        d.append(h, p);
        box.appendChild(d);
    };

    if (m == null) return;

    // Первый зуб на подходе.
    if (m >= 4 && m < 10 && !Object.keys(state.log).length) add('first', 'info');
    // Шестёрка: растёт СЗАДИ и не сменяет никого — её массово принимают за
    // молочную и не лечат.
    if (m >= 60 && m <= 96) add('six_year', 'warn');
    // Акульи зубы: постоянный вылез за молочным.
    if (phase === 'shed' || (m >= 66 && m <= 110)) add('shark', 'info');
    // Затянувшееся отсутствие первого зуба.
    // ⚠️ Ограничено ОКНОМ, а не «старше 15 месяцев»: без верхней границы
    // родителю десятилетнего ребёнка сообщалось, что у него нет ни одного
    // зуба, — просто потому, что он не вёл дневник.
    if (m > 15 && m < 30 && !Object.values(state.log).some(l => l.erupted)) add('none_yet', 'warn');
    // Порядок важнее сроков.
    if (m >= 6 && m <= 36) add('order', 'info');
}

// ── карточка зуба ───────────────────────────────────────────────────────

function openTooth(fdi) {
    const t = CFG.teeth[fdi];
    if (!t || !t.primary) return;

    const phase = effectivePhase();
    const log = state.log[fdi] || {};
    const field = phase === 'shed' ? 'shed' : 'erupted';

    const dlg = $('#tgDialog');
    $('#tgDlgName').textContent = CFG.names[fdi];
    const [a, b] = phase === 'shed' ? t.shed : t.erupt;
    $('#tgDlgWindow').textContent = fmt(T.window_long, {
        from: fmtDate(dateAt(a)), to: fmtDate(dateAt(b)),
        fromM: fmtMonths(a), toM: fmtMonths(b),
    });
    $('#tgDlgSuccessor').textContent = phase === 'shed'
        ? fmt(T.successor, { tooth: CFG.names[t.succeeded_by] || t.succeeded_by })
        : '';
    $('#tgDlgSuccessor').hidden = phase !== 'shed';

    const input = $('#tgDlgDate');
    input.value = log[field] || isoDate(new Date());
    input.max = isoDate(new Date());

    $('#tgDlgSave').onclick = () => {
        state.log[fdi] = Object.assign({}, state.log[fdi], { [field]: input.value });
        save(); render(); dlg.close();
        if (window.__aeFunnel) window.__aeFunnel('tooth_logged', { phase });
    };
    $('#tgDlgClear').onclick = () => {
        if (state.log[fdi]) delete state.log[fdi][field];
        save(); render(); dlg.close();
    };
    $('#tgDlgClear').hidden = !log[field];

    if (typeof dlg.showModal === 'function') dlg.showModal();
}

// ── мелочи ──────────────────────────────────────────────────────────────

function humanAge(m) {
    if (m == null) return '—';
    if (m < 0) return T.age_unborn;
    if (m < 24) return fmt(T.age_months, { n: Math.floor(m) });
    const y = Math.floor(m / 12), rest = Math.floor(m % 12);
    return rest ? fmt(T.age_ym, { y, m: rest }) : fmt(T.age_y, { y });
}

function fmtMonths(m) {
    return m < 30 ? fmt(T.m_short, { n: Math.round(m) })
        : fmt(T.y_short, { n: Math.round(m / 12 * 10) / 10 });
}

function fmtDate(d) {
    return d ? d.toLocaleDateString(window.__LOCALE || 'ru',
        { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function isoDate(d) {
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function fmt(tpl, vars) {
    return String(tpl || '').replace(/:(\w+)/g, (mm, k) => (k in vars ? String(vars[k]) : mm));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
